import { GetPharmacyContactsInfomation, ShippingLabelByPharmacist_shippingLabel } from '@/api/bff_types';
import usePharmacyQuery from '@/hooks/pharmacy/usePharmacyQuery';

import {
  ContactService,
  DeliveryAddress,
  deliveryTypeMap,
  DeliveryTypeMapKeys,
  initialContacts,
  IsAbsentService,
  ShippingLabelByPharmacistService
} from './interface';
import { Order } from '@/apis/order/interface';
import { dayOfWeekMap, DayOfWeekType } from '@/@types/dayofweek';
import { PharmacyDuty } from '@/apis/pharmacy/interface';

/**
 * 배송 및 라벨 프린트
 */
export const PRINT_SHIPPING_LABEL_BUTTON_TYPE = {
  // 부릉 - 새벽 배송
  VROONG_DAWN: 'VROONG_DAWN',
  // 부릉 - 새벽 배송 재출력
  VROONG_DAWN_REPRINT: 'VROONG_DAWN_REPRINT',
  // 부릉 - 새벽 배송 추가 출고
  VROONG_DAWN_ADD_SHIPPING: 'VROONG_DAWN_ADD_SHIPPING',
  // 두발히어로 - 당일 배송
  DOOBAL_HERO_DELIVERY: 'DOOBAL_HERO_DELIVERY',
  // 두발히어로 - 당일 배송 재출력
  DOOBAL_HERO_DELIVERY_REPRINT: 'DOOBAL_HERO_DELIVERY_REPRINT',
  // 팀프레시 - 새벽 배송,
  TEAM_FRESH_DAWN: 'TEAM_FRESH_DAWN',
  // 팀프레시 - 새벽 배송 재출력,
  TEAM_FRESH_DAWN_REPRINT: 'TEAM_FRESH_DAWN_REPRINT'
} as const;

export type PrintShippingLabelButtonType =
  typeof PRINT_SHIPPING_LABEL_BUTTON_TYPE[keyof typeof PRINT_SHIPPING_LABEL_BUTTON_TYPE];

export type TEAM_FRESH_DAWN_TYPES = keyof Pick<
  typeof PRINT_SHIPPING_LABEL_BUTTON_TYPE,
  'TEAM_FRESH_DAWN' | 'TEAM_FRESH_DAWN_REPRINT'
>;

type TEAM_FRESH_RESPONSE_TYPES = {
  [PRINT_SHIPPING_LABEL_BUTTON_TYPE.TEAM_FRESH_DAWN]: {
    deliveryReceipt: ShippingLabelByPharmacist_shippingLabel | null;
  };
  [PRINT_SHIPPING_LABEL_BUTTON_TYPE.TEAM_FRESH_DAWN_REPRINT]: {
    shippingLabel: ShippingLabelByPharmacist_shippingLabel | null;
  };
};

const getTeamFreshDeliveryType = (deliveryType: DeliveryTypeMapKeys) =>
  deliveryTypeMap[deliveryType] || deliveryTypeMap['0'];

const getAddressInfo = (address: DeliveryAddress) =>
  `${address.roadAddress || address.jibunAddress} ${
    address.detailAddress ?? ''
  }`;

const getAreaCode = (areaGroupCode: string) =>
  areaGroupCode ? areaGroupCode[2] : '';

const getLabelPrintData = (
  labelInfo: ShippingLabelByPharmacist_shippingLabel
) => {
  const { deliveryType, sender, receiver, areaGroupCode, ...res } = labelInfo;
  return {
    ...res,
    sender: {
      ...sender,
      address: getAddressInfo(sender.address)
    },
    receiver: {
      ...receiver,
      address: getAddressInfo(receiver.address)
    },
    areaCode: getAreaCode(areaGroupCode),
    areaGroupCode,
    deliveryType: getTeamFreshDeliveryType(deliveryType as DeliveryTypeMapKeys)
  };
};

const getTeamFreshDeliveryReceiptService = (
  response?: TEAM_FRESH_RESPONSE_TYPES['TEAM_FRESH_DAWN']
): ShippingLabelByPharmacistService | null => {
  if (!response?.deliveryReceipt) return null;

  return getLabelPrintData(response.deliveryReceipt);
};

const getTeamFreshShipLabelService = (
  response?: TEAM_FRESH_RESPONSE_TYPES['TEAM_FRESH_DAWN_REPRINT']
): ShippingLabelByPharmacistService | null => {
  if (!response?.shippingLabel) return null;

  return getLabelPrintData(response.shippingLabel);
};

/**
 * 무응답 개선
 */
const getPharmacyContactData = (
  response?: GetPharmacyContactsInfomation
): ContactService => {
  if (!response?.getPharmacy) return initialContacts;

  return initialContacts.map(initContact => {
    const foundContactAliasSame = response.getPharmacy?.pharmacyContacts?.find(
      propContact => propContact.contactAlias === initContact.contactAlias
    );
    return foundContactAliasSame
      ? {
          phone: foundContactAliasSame.phone,
          isActive: true,
          contactAlias: foundContactAliasSame.contactAlias ?? '',
          isSaved: true
        }
      : initContact;
  });
};

const getPharmacyContactsService = () => {
  const { pharmacyContactsInformationQuery } = usePharmacyQuery();
  const { data: pharmacyData } = pharmacyContactsInformationQuery();

  return getPharmacyContactData(pharmacyData);
};

const getPharmacyIsAbsentData = (
  response?: GetPharmacyContactsInfomation
): IsAbsentService => {
  if (!response?.getPharmacy) return { isAbsent: true };

  return { isAbsent: response.getPharmacy.isAbsent };
};

const getPharmacyIsAbsentService = () => {
  const { pharmacyContactsInformationQuery } = usePharmacyQuery();
  const { data: pharmacyData } = pharmacyContactsInformationQuery();

  return getPharmacyIsAbsentData(pharmacyData);
};

const getSymptomLabel = (order: Order): string => {
  const { diagnosisForm } = order.diagnosis;
  if (!diagnosisForm) return '';

  return `${diagnosisForm.diagnosisCategoryType} \n${diagnosisForm.symptom}`;
};

const buildDisplayTodayDuty = (pharmacy: any, todayOfWeekType: DayOfWeekType | undefined): string => {
  const pharmacyDuties = pharmacy.pharmacyDuty as PharmacyDuty[]
  if (!todayOfWeekType || !pharmacyDuties) {
    return ""
  }

  const todayDuty = pharmacyDuties.find(item => item.weekDay === dayOfWeekMap[todayOfWeekType].weekDay) as PharmacyDuty
  return "[" + dayOfWeekMap[todayOfWeekType].name + "]" + " " + todayDuty.openTime.slice(0, 5) + "~" + todayDuty.closeTime.slice(0, 5)
}

export default {
  getTeamFreshDeliveryReceiptService,
  getTeamFreshShipLabelService,
  getPharmacyContactsService,
  getPharmacyIsAbsentService,
  getSymptomLabel,
  buildDisplayTodayDuty
};
